import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Filter, Map as MapIcon, ShoppingBag, BookOpen, GraduationCap, Package, Layers } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import { pageOg } from "@/lib/cdn";
import { fetchWooProducts, decodeHtml } from "@/lib/wooApi";
import { useT } from "@/lib/i18n";

// Each shop category has its own icon and OG image slug used for SEO.
const CATEGORY_META = {
  maps:     { icon: MapIcon,        og: "maps"  },
  books:    { icon: BookOpen,       og: "learn" },
  ebooks:   { icon: BookOpen,       og: "learn" },
  courses:  { icon: GraduationCap,  og: "learn" },
  bundles:  { icon: Layers,         og: "maps"  },
};

const ShopCategoryPage = ({ category }) => {
  const t = useT();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const meta = CATEGORY_META[category] || { icon: ShoppingBag, og: "maps" };
  const title = t(`shop.${category}.title`);
  const sub = t(`shop.${category}.sub`);
  const Icon = meta.icon;

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetchWooProducts({ category, perPage: 50 })
      .then((list) => { if (!cancel) setProducts(list); })
      .catch(() => { if (!cancel) setProducts([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [category]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name.toLowerCase().includes(q));
  }, [products, search]);

  const featured = products[0];

  return (
    <Layout testId={`page-shop-${category}`}>
      <SEO title={`${title} — geoVINUM`} description={sub} image={pageOg(meta.og)} />
      <section className="pt-10 md:pt-16 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("common.home") }, { to: "/shop", label: t("nav.boutique") }, { label: title }]} />
          <div className="mt-5 max-w-3xl flex items-start gap-5">
            <div className="hidden md:flex w-14 h-14 rounded-[5px] bg-[color:var(--gv-mist)] items-center justify-center shrink-0">
              <Icon size={26} className="text-[color:var(--gv-blue)]" />
            </div>
            <div>
              <div className="eyebrow mb-3">{t("nav.boutique")}</div>
              <h1 className="font-display text-[42px] md:text-[60px] leading-[1.05] text-[color:var(--gv-ink)] mb-3" data-testid="shop-title">
                {title} <span aria-hidden>🍷</span>
              </h1>
              <p className="text-[16.5px] leading-[1.7] text-[color:var(--gv-ink)]/75 max-w-xl">{sub}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      {loading && (
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-[color:var(--gv-mist)] rounded-[6px] p-6 md:p-10">
            <div className="lg:col-span-5 space-y-3">
              <div className="h-3 w-20 bg-white rounded animate-pulse" />
              <div className="h-9 w-3/4 bg-white rounded animate-pulse" />
              <div className="h-4 w-full bg-white rounded animate-pulse" />
            </div>
            <div className="lg:col-span-7 aspect-[4/3] bg-white rounded-[5px] animate-pulse" />
          </div>
        </section>
      )}

      {!loading && featured && (
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <a
              href={featured.permalink}
              target="_blank"
              rel="noreferrer"
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-[color:var(--gv-mist)] rounded-[6px] p-6 md:p-10 reveal hover:opacity-95 transition-opacity"
              data-testid={`shop-featured-${featured.id}`}
            >
              <div className="lg:col-span-5">
                <div className="eyebrow mb-3">{t("maps.featured_eyebrow")}</div>
                <h2 className="font-display text-[28px] md:text-[40px] leading-[1.1] text-[color:var(--gv-ink)] mb-4">
                  {decodeHtml(featured.name)}
                </h2>
                {featured.short_description && (
                  <p className="text-[15.5px] text-[color:var(--gv-ink)]/70 mb-6 line-clamp-4">{featured.short_description}</p>
                )}
                {featured.price && <div className="font-display text-[24px] text-[color:var(--gv-ink)] mb-6">€{featured.price}</div>}
                <span className="btn-primary inline-flex">{t("cta.buy_now")} <ArrowRight size={16} /></span>
              </div>
              <div className="lg:col-span-7">
                <div className="rounded-[6px] overflow-hidden shadow-[0_30px_80px_-30px_rgba(46,80,103,0.35)] bg-white">
                  {featured.image
                    ? <img src={featured.image} alt={decodeHtml(featured.name)} className="w-full h-[360px] md:h-[480px] object-cover" />
                    : <div className="w-full h-[360px] md:h-[480px] flex items-center justify-center"><Icon size={56} className="text-[color:var(--gv-blue)]/30" /></div>}
                </div>
              </div>
            </a>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <h2 className="font-display text-[26px] md:text-[34px] text-[color:var(--gv-ink)]">{t("maps.all_pieces")}</h2>
            <div className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="inline-flex items-center gap-1.5 text-[color:var(--gv-ink)]/60 mr-2"><Filter size={13} /> {t("maps.filter")}</span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("common.search")}
                className="h-9 px-3 rounded-[5px] border border-[color:var(--gv-line)] text-[13px] bg-white focus:border-[color:var(--gv-pink)] outline-none"
                data-testid="shop-search-input"
              />
            </div>
          </div>

          {loading && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="bg-white rounded-[5px] overflow-hidden border border-[color:var(--gv-line)]">
                  <div className="aspect-[3/4] bg-[color:var(--gv-mist)] animate-pulse" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 w-1/3 bg-[color:var(--gv-mist)] rounded animate-pulse" />
                    <div className="h-4 w-4/5 bg-[color:var(--gv-mist)] rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {filtered.map((p) => (
                <a
                  key={p.id}
                  href={p.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="map-card block bg-white rounded-[5px] overflow-hidden border border-[color:var(--gv-line)] hover:border-[color:var(--gv-pink)] transition-colors"
                  data-testid={`shop-product-${p.id}`}
                >
                  <div className="aspect-[3/4] overflow-hidden bg-[color:var(--gv-mist)]">
                    {p.image
                      ? <img src={p.image} alt={decodeHtml(p.name)} className="w-full h-full object-cover" loading="lazy" />
                      : <div className="w-full h-full flex items-center justify-center"><Icon size={32} className="text-[color:var(--gv-blue)]/30" /></div>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/50">{title}</span>
                      {p.price && <span className="text-[12.5px] font-medium text-[color:var(--gv-pink)]">€{p.price}</span>}
                    </div>
                    <div className="font-display text-[15.5px] text-[color:var(--gv-ink)] leading-snug line-clamp-2">{decodeHtml(p.name)}</div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && products.length === 0 && (
            <div className="border border-dashed border-[color:var(--gv-line)] rounded-[6px] p-12 text-center" data-testid="shop-empty">
              <Package size={28} className="mx-auto text-[color:var(--gv-blue)]/40 mb-3" />
              <div className="font-display text-[20px] text-[color:var(--gv-ink)] mb-1">{t("shop.empty")}</div>
            </div>
          )}

          {!loading && filtered.length === 0 && products.length > 0 && (
            <div className="text-center py-12 text-[color:var(--gv-ink)]/60">{t("maps.no_pieces")}</div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ShopCategoryPage;
